/**
 * Turning what the app holds into a report.
 *
 * Each builder is a pure function from data the screens already fetch to the
 * block vocabulary in ./types. Nothing here queries anything: a report is a view
 * of the same numbers the coach has been looking at all week, which is the only
 * way the printed page can be trusted to agree with the screen.
 */

import type { Report, ReportBlock, ReportMeta } from './types';
import { ageLabel, formatBirthDate, formatDate, formatDateRange, sheetName } from '@/lib/format';
import { configLabel } from '@/lib/labels';
import type {
  BatteryRow,
  CandidateRow,
  DevelopmentSquadRow,
  EvaluationListRow,
  PlayerDevelopment,
  TrialRow,
  TrialSessionRow,
} from '@/app/queries';
import type { PlayerRecord } from '@shared/schemas';
import { ATTENDANCE_MARK, registerFitsOnePage } from '@/lib/attendanceSheet';
import { chunkTests, longestWordIn, testingSheetLayout } from '@/lib/testingSheet';
import type { RegisterColumn, RegisterPlayer } from '@/lib/attendanceSheet';

type T = (key: string, values?: Record<string, string | number>) => string;

export interface BuildContext {
  meta: Omit<ReportMeta, 'title' | 'subtitle'>;
  locale: string;
  t: T;
}

const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

/** The squad as it stands: the list a coach hands to anyone who asks for one. */
export function squadReport(
  ctx: BuildContext,
  players: PlayerRecord[],
  positions: { key: string; label: string; i18nKey?: string | null }[],
  fields: { key: string; label: string; isSensitive: boolean }[],
  options: { includeSensitive: boolean },
): Report {
  const { t, locale } = ctx;
  const positionLabel = (key: string | null) => {
    const found = positions.find((p) => p.key === key);
    if (!found) return '—';
    const translated = found.i18nKey ? t(found.i18nKey) : found.label;
    return translated === found.i18nKey ? found.label : translated;
  };

  // A printed squad list goes on a noticeboard. Contact details stay off it
  // unless the coach deliberately asks, because most of these players are minors.
  const shown = fields.filter((f) => options.includeSensitive || !f.isSensitive);

  return {
    meta: { ...ctx.meta, title: t('report.squad'), subtitle: ctx.meta.teamName },
    blocks: [
      {
        kind: 'facts',
        facts: [
          { label: t('report.playerCount'), value: String(players.length) },
          {
            label: t('report.goalkeepers'),
            value: String(players.filter((p) => p.primaryPositionKey === 'GK').length),
          },
        ],
      },
      {
        kind: 'table',
        columns: [
          { key: 'no', label: t('squad.number'), width: '12mm', align: 'end' },
          { key: 'name', label: t('squad.player') },
          { key: 'pos', label: t('squad.position'), width: '34mm' },
          { key: 'born', label: t('squad.dateOfBirth'), width: '24mm' },
          { key: 'age', label: t('squad.age'), width: '14mm', align: 'end' },
          ...shown.map((f) => ({ key: `f_${f.key}`, label: f.label })),
        ],
        // A printed squad list reads in shirt-number order, the way a team
        // sheet is printed; unnumbered players fall to the end.
        rows: [...players]
          .sort(
            (a, b) =>
              (a.shirtNumber ?? Number.POSITIVE_INFINITY) -
                (b.shirtNumber ?? Number.POSITIVE_INFINITY) ||
              a.lastName.localeCompare(b.lastName, locale),
          )
          .map((p) => ({
          no: dash(p.shirtNumber),
          name: sheetName(p.knownAs ?? p.firstName, p.lastName),
          pos: positionLabel(p.primaryPositionKey),
          born: p.dateOfBirth ? formatBirthDate(p.dateOfBirth, locale) : '—',
          age: dash(ageLabel(p.dateOfBirth)),
          ...Object.fromEntries(
            shown.map((f) => [`f_${f.key}`, dash(p.attributes?.[f.key])]),
          ),
        })),
        empty: t('squad.empty'),
      },
    ],
  };
}

/** One player's development file, as the page you take into the conversation. */
export function playerReport(ctx: BuildContext, file: PlayerDevelopment): Report {
  const { t, locale } = ctx;
  const player = file.player;
  const name = player ? sheetName(player.knownAs ?? player.firstName, player.lastName) : '—';
  const latest = file.evaluations[file.evaluations.length - 1];

  return {
    meta: { ...ctx.meta, title: t('report.player'), subtitle: name },
    blocks: [
      {
        kind: 'facts',
        facts: [
          { label: t('squad.position'), value: dash(player?.positionKey) },
          { label: t('squad.dateOfBirth'), value: player?.dateOfBirth ? formatBirthDate(player.dateOfBirth, locale) : '—' },
          { label: t('squad.age'), value: dash(ageLabel(player?.dateOfBirth)) },
          {
            label: t('development.readiness'),
            value: file.latestReadiness
              ? t(`development.readinessValue.${file.latestReadiness}`)
              : t('development.readinessValue.unset'),
          },
        ],
      },
      {
        kind: 'table',
        title: t('evaluation.title'),
        columns: [
          { key: 'date', label: t('evaluation.date'), width: '26mm' },
          { key: 'context', label: t('evaluation.context') },
          ...['technical', 'tactical', 'physical', 'mental', 'social'].map((d) => ({
            key: d,
            label: t(`development.dimension.${d}`),
            align: 'end' as const,
            width: '18mm',
          })),
        ],
        rows: file.evaluations.map((e) => ({
          date: formatDate(e.onDate, locale),
          context: dash(e.context),
          ...Object.fromEntries(
            ['technical', 'tactical', 'physical', 'mental', 'social'].map((d) => [
              d,
              e.averages[d] !== undefined ? e.averages[d]!.toFixed(1) : '—',
            ]),
          ),
        })),
        empty: t('evaluation.empty'),
      },
      {
        kind: 'table',
        title: t('testing.title'),
        columns: [
          { key: 'test', label: t('testing.name') },
          { key: 'latest', label: t('development.latest'), align: 'end', width: '22mm' },
          { key: 'on', label: t('evaluation.date'), width: '26mm' },
          { key: 'trend', label: t('development.trend'), width: '26mm' },
        ],
        rows: file.testing.map((row) => ({
          test: configLabel(row.test, t),
          // Isolated so a measurement reads left-to-right inside Arabic text;
          // without it "4.21 s" comes out as "s 4.21".
          latest: `\u2066${row.latest.value}${row.test.unit ? ` ${row.test.unit}` : ''}\u2069`,
          on: formatDate(row.latest.onDate, locale),
          trend:
            row.trend.trend === 'improving'
              ? t('development.trendImproving')
              : row.trend.trend === 'declining'
                ? t('development.trendDeclining')
                : row.trend.trend === 'stable'
                  ? t('development.trendStable')
                  : t('development.trendUnknown'),
        })),
        empty: t('development.noHistory'),
      },
      {
        kind: 'prose',
        title: t('evaluation.summary'),
        paragraphs: [latest?.summary, latest?.strengths, latest?.weaknesses],
      },
      { kind: 'note', text: t('report.judgementNote') },
    ],
  };
}

/** A trial, with the list at the end and the reasons behind it. */
export function trialReport(
  ctx: BuildContext,
  trial: TrialRow,
  candidates: CandidateRow[],
  sessions: TrialSessionRow[],
): Report {
  const { t, locale } = ctx;
  return {
    meta: { ...ctx.meta, title: t('report.trial'), subtitle: trial.name },
    blocks: [
      {
        kind: 'facts',
        facts: [
          { label: t('trial.startsOn'), value: formatDate(trial.startsOn, locale) },
          { label: t('trial.location'), value: dash(trial.location) },
          { label: t('trial.candidatesLabel'), value: String(candidates.length) },
          { label: t('trial.sessionName'), value: String(sessions.length) },
        ],
      },
      { kind: 'prose', title: t('trial.criteria'), paragraphs: [trial.criteria] },
      {
        kind: 'table',
        title: t('trial.candidatesLabel'),
        columns: [
          { key: 'bib', label: t('trial.bib'), width: '12mm', align: 'end' },
          { key: 'name', label: t('trial.candidate') },
          { key: 'pos', label: t('squad.position'), width: '18mm' },
          { key: 'seen', label: t('trial.seenLabel'), width: '16mm', align: 'end' },
          { key: 'overall', label: t('trial.overall'), width: '18mm', align: 'end' },
          { key: 'standing', label: t('trial.standing'), width: '26mm' },
          { key: 'reason', label: t('trial.decisionReason') },
        ],
        rows: candidates.map((c) => ({
          bib: dash(c.bibNumber),
          name: sheetName(c.knownAs ?? c.firstName, c.lastName),
          pos: dash(c.primaryPositionKey),
          seen: String(c.summary.attended),
          overall: c.summary.overall !== null ? c.summary.overall.toFixed(1) : '—',
          standing: t(`trial.standingValue.${c.standing.standing}`),
          reason: dash(c.decisionReason ?? t(c.standing.reason)),
        })),
        empty: t('trial.noCandidates'),
      },
      { kind: 'note', text: t('report.trialNote') },
    ],
  };
}

/**
 * What a testing day produced, for the file and for the physical coach.
 *
 * The whole squad, not only the players with a number against them. A testing
 * day where six were absent is a fact about the day, and a column of dashes
 * says it where a shorter list hides it — the same reason D-048 keeps the
 * minutes plan a grid. The tests are the day's own list, resolved by the
 * caller, so a day with nothing entered yet still prints the sheet it will be.
 */
export function testingReport(
  ctx: BuildContext,
  battery: BatteryRow,
  squad: DevelopmentSquadRow[],
  tests: { key: string; label: string; i18nKey?: string | null; unit: string | null }[],
  results: { playerId: string; testKey: string; value: number | null }[],
): Report {
  const { t, locale } = ctx;
  const byPlayer = new Map<string, Record<string, number | null>>();
  for (const r of results) {
    if (r.value === null) continue;
    const entry = byPlayer.get(r.playerId) ?? {};
    entry[r.testKey] = r.value;
    byPlayer.set(r.playerId, entry);
  }

  // The same arithmetic as the blank sheet, so the page a coach types up looks
  // like the page he carried: same orientation, same column widths, same
  // headings on one line.
  const layout = testingSheetLayout(
    tests.length,
    squad.length,
    longestWordIn(tests.map((x) => configLabel(x, t))),
  );

  return {
    meta: { ...ctx.meta, title: t('report.testing'), subtitle: battery.name },
    orientation: layout.orientation,
    blocks: [
      {
        kind: 'facts',
        facts: [
          { label: t('testing.date'), value: formatDate(battery.onDate, locale) },
          { label: t('testing.conditions'), value: dash(battery.conditions) },
          {
            label: t('report.playerCount'),
            // Of the squad, not on its own: "12" alone reads as the squad size
            // on a page that also lists twenty-four names.
            value: t('report.testedOf', { tested: byPlayer.size, total: squad.length }),
          },
        ],
      },
      // Chunked for the same reason the blank sheet is: past what a page holds
      // at a legible size, a fourteenth column is not a narrower column, it is
      // a word lying across the one beside it.
      ...chunkTests(tests, layout.perSheet).map((group, i, all) => ({
        kind: 'table' as const,
        evenColumns: true,
        headerFont: `${layout.headerFont.toFixed(2)}mm`,
        breakBefore: i > 0,
        ...(all.length > 1
          ? { title: t('form.testingSheetOf', { n: i + 1, of: all.length }) }
          : {}),
        columns: [
          { key: 'no', label: t('squad.number'), width: layout.numberWidth, align: 'end' as const },
          { key: 'name', label: t('evaluation.player'), width: layout.nameWidth },
          ...group.map((x) => ({
            key: x.key,
            // The unit under the name rather than in brackets after it: in a
            // column sized for the name, "(cm)" is what pushes the heading on
            // to another line.
            label: `${configLabel(x, t)}${x.unit ? `\n${x.unit}` : ''}`,
            align: 'end' as const,
            headerAlign: 'center' as const,
          })),
        ],
        rows: squad.map((p) => ({
          no: dash(p.shirtNumber),
          name: sheetName(p.knownAs ?? p.firstName, p.lastName),
          ...Object.fromEntries(
            group.map((x) => [x.key, dash(byPlayer.get(p.playerId)?.[x.key])]),
          ),
        })),
        empty: t('squad.empty'),
      })),
      // Said on the page rather than left as a grid of dashes to interpret: a
      // report that looks broken and a report with nothing in it yet are
      // indistinguishable to whoever is handed it.
      ...(byPlayer.size === 0
        ? [{ kind: 'note' as const, text: t('report.testingNothing') }]
        : []),
      { kind: 'note', text: t('report.testingNote') },
    ],
  };
}

/** The evaluations a team recorded over a period. */
export function evaluationsReport(
  ctx: BuildContext,
  rows: EvaluationListRow[],
): Report {
  const { t, locale } = ctx;
  return {
    meta: { ...ctx.meta, title: t('report.development'), subtitle: ctx.meta.teamName },
    blocks: [
      {
        kind: 'table',
        columns: [
          { key: 'date', label: t('evaluation.date'), width: '26mm' },
          { key: 'name', label: t('evaluation.player') },
          ...['technical', 'tactical', 'physical', 'mental', 'social'].map((d) => ({
            key: d,
            label: t(`development.dimension.${d}`),
            align: 'end' as const,
            width: '17mm',
          })),
          { key: 'readiness', label: t('development.readiness'), width: '28mm' },
        ],
        rows: rows.map((r) => ({
          date: formatDate(r.onDate, locale),
          name: sheetName(r.knownAs ?? r.firstName, r.lastName),
          ...Object.fromEntries(
            ['technical', 'tactical', 'physical', 'mental', 'social'].map((d) => [
              d,
              r.averages[d] !== undefined ? r.averages[d]!.toFixed(1) : '—',
            ]),
          ),
          readiness: r.seniorReadiness
            ? t(`development.readinessValue.${r.seniorReadiness}`)
            : '—',
        })),
        empty: t('evaluation.empty'),
      },
      { kind: 'note', text: t('report.judgementNote') },
    ],
  };
}

/**
 * The paper register: players down the side, days across, and room to write.
 *
 * Deliberately empty. A coach marks this on a clipboard at the side of a pitch
 * and types it up afterwards — or photographs it and brings it back in through
 * the attendance import. Printing a sheet that already holds the answers would
 * be a different document.
 */
export function registerReport(
  ctx: BuildContext,
  players: RegisterPlayer[],
  columns: RegisterColumn[],
  period: { from: string; to: string },
  /**
   * What is already recorded, keyed `playerId|date`.
   *
   * A register printed for a month that is half gone has to carry the half that
   * happened, or it is a sheet the coach has to reconcile by hand against a
   * screen. Recorded days print their mark; the rest stay boxes to write in.
   */
  recorded: Record<string, string> = {},
): Report {
  const { t, locale } = ctx;
  // One squad, one sheet, one clipboard. The rows divide whatever height the page
  // has left instead of each taking a fixed one, so a squad of thirty and a squad
  // of eighteen both come out as a single page that is fully used. A squad too
  // big for that drops back to ordinary pagination rather than shrinking the
  // writing boxes past the point of being writable.
  const onePage = registerFitsOnePage(players.length);
  return {
    meta: {
      ...ctx.meta,
      title: t('report.register'),
      subtitle: `${formatDate(period.from, locale)} – ${formatDate(period.to, locale)}`,
    },
    // 31 columns of writing boxes only fit the long way round.
    orientation: 'landscape',
    ...(onePage ? { fit: 'page' as const } : {}),
    blocks: [
      {
        kind: 'table',
        grid: true,
        stretch: onePage,
        columns: [
          { key: 'no', label: t('squad.number'), width: '9mm', align: 'end' },
          { key: 'name', label: t('squad.player'), width: '52mm' },
          ...columns.map((c) => ({
            key: c.date,
            // Day number over weekday: the two things a coach scans for. A
            // match day adds a third line, because on paper there is no colour
            // to tell a Saturday fixture from a Saturday off.
            label:
              c.kind === 'match'
                ? `${c.day}\n${c.weekday}\n${t('register.matchShort')}`
                : `${c.day}\n${c.weekday}`,
            align: 'start' as const,
          })),
        ],
        rows: players.map((p) => ({
          no: p.shirtNumber ?? '',
          name: sheetName(p.name.split(' ')[0] ?? p.name, p.name.split(' ').slice(1).join(' ')),
          ...Object.fromEntries(
            columns.map((c) => [c.date, ATTENDANCE_MARK[recorded[`${p.id}|${c.date}`] ?? ''] ?? '']),
          ),
        })),
        empty: t('squad.empty'),
      },
      { kind: 'note', text: `${t('report.registerNote')} ${t('register.matchNote')}` },
    ],
  };
}

export interface CyclePlanRow {
  id: string;
  name: string;
  startsOn: string;
  endsOn: string;
  dominantTheme: string | null;
  secondaryTheme: string | null;
  objectiveSummary: string | null;
  diagnosis: string | null;
  evidence: string | null;
  rationale: string | null;
  principleIds: string[];
  physicalEmphasis: string[];
  successCriteria: string | null;
  evaluationMethod: string | null;
  modifyIf: string | null;
  reviewOn: string | null;
  outcome: string | null;
  objectives: { dimension: string | null; title: string; detail: string | null }[];
}

/**
 * A block, on one page, for somebody who was not in the room.
 *
 * Ordered as the argument runs rather than as the database stores it: what we
 * saw, what it rests on, why this block, what we are chasing, how we will know,
 * and what would change it. A director reading top to bottom gets the reasoning,
 * not a list of fields.
 */
export function cycleReport(
  ctx: BuildContext,
  cycle: CyclePlanRow,
  context: {
    kindLabel: string;
    principles: { id: string; name: string; phase: string }[];
    qualities: { key: string; label: string; i18nKey?: string | null }[];
    weeks: { name: string; startsOn: string; endsOn: string; objectiveSummary: string | null }[];
  },
): Report {
  const { t, locale } = ctx;
  const named = (dimension: string) =>
    cycle.objectives.find((o) => (o.dimension ?? 'team') === dimension);

  const principleNames = cycle.principleIds
    .map((id) => context.principles.find((p) => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p))
    .map((p) => `${p.name} (${t(`gameModel.phase_${p.phase}`)})`);

  const qualityNames = cycle.physicalEmphasis.map((key) => {
    const found = context.qualities.find((q) => q.key === key);
    return found ? configLabel(found, t) : key;
  });

  return {
    meta: {
      ...ctx.meta,
      title: `${context.kindLabel} — ${cycle.name}`,
      subtitle: formatDateRange(cycle.startsOn, cycle.endsOn, locale),
    },
    blocks: [
      {
        kind: 'facts',
        // Themes only appear when the block has them. The form does not ask for
        // them — they arrive on imported and seeded blocks — and a printed page
        // handed to a board should not carry two labelled blanks for fields its
        // author was never offered.
        facts: [
          ...(cycle.dominantTheme
            ? [{ label: t('cycle.dominantTheme'), value: cycle.dominantTheme }]
            : []),
          ...(cycle.secondaryTheme
            ? [{ label: t('cycle.secondaryTheme'), value: cycle.secondaryTheme }]
            : []),
          {
            label: t('cycle.reviewOn'),
            value: cycle.reviewOn ? formatDate(cycle.reviewOn, locale) : '—',
          },
        ],
      },
      { kind: 'prose', title: t('cycle.diagnosis'), paragraphs: [cycle.diagnosis] },
      { kind: 'prose', title: t('cycle.evidence'), paragraphs: [cycle.evidence] },
      { kind: 'prose', title: t('cycle.rationale'), paragraphs: [cycle.rationale] },
      {
        kind: 'table',
        title: t('cycle.objectives'),
        columns: [
          { key: 'dimension', label: t('cycle.dimension'), width: '34mm' },
          { key: 'title', label: t('cycle.objective') },
        ],
        rows: ['team', 'technical', 'tactical', 'physical', 'mental', 'social']
          .map((d) => ({ d, o: named(d) }))
          .filter((x) => x.o)
          .map((x) => ({
            dimension:
              x.d === 'team' ? t('cycle.teamObjective') : t(`development.dimension.${x.d}`),
            title: x.o!.detail ? `${x.o!.title} — ${x.o!.detail}` : x.o!.title,
          })),
        empty: t('cycle.noObjectives'),
      },
      {
        kind: 'facts',
        title: t('cycle.emphasis'),
        facts: [
          { label: t('cycle.principles'), value: principleNames.join(' · ') || '—' },
          { label: t('cycle.physical'), value: qualityNames.join(' · ') || '—' },
        ],
      },
      { kind: 'prose', title: t('cycle.successCriteria'), paragraphs: [cycle.successCriteria] },
      { kind: 'prose', title: t('cycle.evaluationMethod'), paragraphs: [cycle.evaluationMethod] },
      // Last and on its own, because it is the part that makes the rest a plan
      // rather than a prediction.
      { kind: 'prose', title: t('cycle.modifyIf'), paragraphs: [cycle.modifyIf] },
      { kind: 'prose', title: t('cycle.outcome'), paragraphs: [cycle.outcome] },
      {
        kind: 'table',
        title: t('cycle.weeks'),
        columns: [
          { key: 'dates', label: t('cycle.period'), width: '46mm' },
          { key: 'name', label: t('cycle.name'), width: '50mm' },
          { key: 'objective', label: t('cycle.objective') },
        ],
        rows: context.weeks.map((w) => ({
          dates: formatDateRange(w.startsOn, w.endsOn, locale),
          name: w.name,
          objective: dash(w.objectiveSummary),
        })),
        empty: t('cycle.noWeeks'),
      },
      { kind: 'note', text: t('cycle.reportNote') },
    ],
  };
}

// ── Forms ──────────────────────────────────────────────────────────────
//
// Blank sheets, carried to the pitch and written on in biro. Every one of them
// is the paper counterpart of a screen that already exists: a coach standing in
// the rain is not going to take a phone out, and what he writes on the touchline
// is typed up afterwards or it is lost. These are not reports — they carry no
// data — so they are built from the same blocks and say so on the page.

/** Rows left empty, for a form that is filled in by hand. */
function blankRows(count: number, columns: readonly string[]) {
  return Array.from({ length: count }, () =>
    Object.fromEntries(columns.map((key) => [key, ''])),
  );
}

/**
 * The session, judged while it is still in the legs.
 *
 * Exercise by exercise, because "the session was fine" is not something next
 * week can be planned from, and the one question that matters is whether the
 * exercise did what it was chosen to do.
 */
export function sessionFormReport(ctx: BuildContext): Report {
  const { t } = ctx;
  return {
    meta: { ...ctx.meta, title: t('form.session'), subtitle: t('form.sessionSubtitle') },
    blocks: [
      {
        kind: 'table',
        grid: true,
        rowHeight: '9mm',
        columns: [
          { key: 'field', label: t('form.about'), width: '34mm' },
          { key: 'value', label: '' },
        ],
        rows: [
          { field: t('form.date'), value: '' },
          { field: t('form.mdLabel'), value: '' },
          { field: t('form.objective'), value: '' },
        ],
      },
      {
        kind: 'table',
        title: t('form.exercises'),
        grid: true,
        rowHeight: '13mm',
        columns: [
          { key: 'exercise', label: t('form.exercise'), width: '38mm' },
          { key: 'intended', label: t('form.intended') },
          { key: 'happened', label: t('form.happened') },
          { key: 'verdict', label: t('form.keepOrChange'), width: '34mm' },
        ],
        rows: blankRows(5, ['exercise', 'intended', 'happened', 'verdict']),
      },
      {
        kind: 'table',
        title: t('form.howHard'),
        grid: true,
        rowHeight: '11mm',
        columns: [
          { key: 'rpe', label: t('form.ringTheRating'), width: '60mm' },
          { key: 'change', label: t('form.changeNextTime') },
        ],
        rows: [{ rpe: '1  2  3  4  5  6  7  8  9  10', change: '' }],
      },
      { kind: 'note', text: t('form.sessionNote') },
    ],
  };
}

/**
 * The match, in the four phases the analysis screen asks for.
 *
 * Written in the dugout and typed in afterwards, so the headings are the same
 * ones the screen uses — a sheet that asks different questions from the app is a
 * second system to reconcile.
 */
export function matchFormReport(ctx: BuildContext): Report {
  const { t } = ctx;
  const phase = (key: string) => t(`gameModel.phase_${key}`);
  return {
    meta: { ...ctx.meta, title: t('form.match'), subtitle: t('form.matchSubtitle') },
    blocks: [
      {
        kind: 'table',
        grid: true,
        rowHeight: '9mm',
        columns: [
          { key: 'opponent', label: t('calendar.opponent') },
          { key: 'date', label: t('form.date'), width: '32mm' },
          { key: 'score', label: t('form.score'), width: '24mm' },
        ],
        rows: [{ opponent: '', date: '', score: '' }],
      },
      {
        kind: 'table',
        title: t('match.whatWeWantToSee'),
        grid: true,
        rowHeight: '14mm',
        columns: [{ key: 'seen', label: '' }],
        rows: blankRows(1, ['seen']),
      },
      {
        kind: 'table',
        title: t('form.whatWeSaw'),
        grid: true,
        rowHeight: '17mm',
        columns: [
          { key: 'phase', label: t('form.phase'), width: '34mm' },
          { key: 'notes', label: '' },
        ],
        rows: [
          { phase: phase('with_ball'), notes: '' },
          { phase: phase('without_ball'), notes: '' },
          { phase: phase('attacking_transition'), notes: '' },
          { phase: phase('defensive_transition'), notes: '' },
          { phase: phase('set_pieces'), notes: '' },
        ],
      },
      {
        kind: 'table',
        title: t('match.problems'),
        grid: true,
        rowHeight: '11mm',
        columns: [
          { key: 'problem', label: t('form.problem') },
          { key: 'becomes', label: t('form.becomes') },
        ],
        rows: blankRows(3, ['problem', 'becomes']),
      },
      { kind: 'note', text: t('form.matchNote') },
    ],
  };
}

/**
 * A testing day on paper: the squad down the side, one column per test.
 *
 * Landscape past four tests, because a testing sheet with a column too narrow to
 * write 4.32 in is a sheet that gets rewritten on somebody's hand.
 */
export function testingFormReport(
  ctx: BuildContext,
  battery: { name: string } | null,
  squad: { firstName: string; lastName: string; shirtNumber?: number | null }[],
  tests: { key: string; label: string; i18nKey?: string | null; unit: string | null }[],
): Report {
  const { t } = ctx;
  // Without a battery chosen the sheet is still useful — six blank columns to
  // write the tests into, which is what a coach improvising a testing day needs.
  const chosen = tests.length
    ? tests.map((x) => ({
        key: x.key,
        // The unit under the name, so nobody has to remember whether this one is
        // seconds or metres while holding a stopwatch.
        label: `${configLabel(x, t)}${x.unit ? `\n${x.unit}` : ''}`,
      }))
    : Array.from({ length: 6 }, (_, i) => ({ key: `t${i + 1}`, label: '' }));

  /*
    The shape is arithmetic, not a guess (`src/lib/testingSheet.ts`).

    It was `columns.length > 6 ? 'landscape' : 'portrait'` and nothing else, so
    a squad of twenty-four and eight tests came out 250mm tall on a 210mm page:
    four names on a second sheet, which is exactly the sheet nobody carries to
    the pitch. The layout now decides the orientation from the width the
    columns actually need, squeezes the name column only once the measure
    columns are the thing under pressure, and tells the report whether this many
    players can share one page at all.
  */
  const layout = testingSheetLayout(
    chosen.length,
    squad.length,
    longestWordIn(chosen.map((c) => c.label)),
  );
  const sheets = chunkTests(chosen, layout.perSheet);

  return {
    meta: {
      ...ctx.meta,
      title: t('form.testing'),
      subtitle: battery?.name ?? t('form.testingSubtitle'),
    },
    orientation: layout.orientation,
    // Only when it is true. A sheet told to be exactly one page crops whatever
    // does not fit, and a testing sheet missing its last four players is worse
    // than one on two pages (D-039).
    ...(layout.onePage ? { fit: 'page' as const } : {}),
    blocks: [
      ...sheets.map((columns, i) => ({
        kind: 'table' as const,
        grid: true,
        evenColumns: true,
        headerFont: `${layout.headerFont.toFixed(2)}mm`,
        // One sheet of columns fills the page; a battery too wide for one takes
        // a second rather than shrinking the writing box to nothing.
        stretch: layout.onePage && sheets.length === 1,
        ...(layout.onePage ? {} : { rowHeight: '6.5mm' }),
        columns: [
          { key: 'no', label: t('squad.number'), width: layout.numberWidth, align: 'end' as const },
          { key: 'name', label: t('squad.player'), width: layout.nameWidth },
          // Centred over the box it heads, so a one-line and a three-line name
          // read as one band rather than a ragged edge.
          ...columns.map((c) => ({ ...c, headerAlign: 'center' as const })),
        ],
        rows: squad.map((p) => ({
          no: p.shirtNumber ?? '',
          name: sheetName(p.firstName, p.lastName),
          ...Object.fromEntries(columns.map((x) => [x.key, ''])),
        })),
        empty: t('squad.empty'),
        ...(sheets.length > 1
          ? {
              title: t('form.testingSheetOf', { n: i + 1, of: sheets.length }),
              // Each sheet is a sheet. Flowing them together puts a third page
              // under two that are two-thirds full.
              breakBefore: i > 0,
            }
          : {}),
      })),
      { kind: 'note', text: t('form.testingNote') },
    ],
  };
}

/**
 * The five dimensions against the squad, blank.
 *
 * The same five the app evaluates on, in the same order, so a sheet filled in on
 * a Saturday types into the player file on Sunday without translation.
 */
export function evaluationFormReport(
  ctx: BuildContext,
  squad: { firstName: string; lastName: string; shirtNumber?: number | null }[],
): Report {
  const { t } = ctx;
  const dimensions = ['technical', 'tactical', 'physical', 'mental', 'social'] as const;
  return {
    meta: { ...ctx.meta, title: t('form.evaluation'), subtitle: t('form.evaluationSubtitle') },
    // Portrait, because the page has to be long rather than wide: seven columns
    // fit across A4, and a squad of twenty-four does not fit down a landscape one.
    orientation: 'portrait',
    blocks: [
      {
        kind: 'table',
        grid: true,
        rowHeight: '6mm',
        columns: [
          { key: 'no', label: t('squad.number'), width: '9mm', align: 'end' },
          { key: 'name', label: t('squad.player'), width: '42mm' },
          ...dimensions.map((d) => ({
            key: d,
            label: t(`development.dimension.${d}`),
            width: '17mm',
          })),
          { key: 'note', label: t('form.oneThing') },
        ],
        rows: squad.map((p) => ({
          no: p.shirtNumber ?? '',
          name: sheetName(p.firstName, p.lastName),
          ...Object.fromEntries(dimensions.map((d) => [d, ''])),
          note: '',
        })),
        empty: t('squad.empty'),
      },
      { kind: 'note', text: t('form.evaluationNote') },
    ],
  };
}
